import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { ArrowLeft, Upload, Download, Volume2, VolumeX } from 'lucide-react';
import { toast } from 'sonner';
import { QRCodeSVG } from 'qrcode.react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ExhibitForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();
  const isEdit = !!id;

  const [formData, setFormData] = useState({
    title_tc: '',
    title_en: '',
    title_sc: '',
    description_tc: '',
    description_en: '',
    description_sc: '',
    voice_url_tc: '',
    voice_url_en: '',
    voice_url_sc: '',
  });
  const [initialLoad, setInitialLoad] = useState(false);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [qrCodeUrl, setQrCodeUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [translating, setTranslating] = useState(false);
  const [playingAudio, setPlayingAudio] = useState(null); // 'tc', 'en', 'sc', or null
  const [exhibitCode, setExhibitCode] = useState(null);

  useEffect(() => {
    if (isEdit) {
      fetchExhibit();
    }
  }, [id]);

  const fetchExhibit = async () => {
    try {
      const response = await axios.get(`${API}/staff/exhibits/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const exhibit = response.data;
      if (exhibit) {
        setFormData({
          title_tc: exhibit.title_tc || '',
          title_en: exhibit.title_en || '',
          title_sc: exhibit.title_sc || '',
          description_tc: exhibit.description_tc || '',
          description_en: exhibit.description_en || '',
          description_sc: exhibit.description_sc || '',
          voice_url_tc: exhibit.voice_url_tc || '',
          voice_url_en: exhibit.voice_url_en || '',
          voice_url_sc: exhibit.voice_url_sc || '',
        });
        if (exhibit.image_url) {
          // Add cache-busting timestamp to prevent showing cached old image
          const timestamp = exhibit.updated_at ? new Date(exhibit.updated_at).getTime() : Date.now();
          setImagePreview(`${API}${exhibit.image_url}?t=${timestamp}`);
        }
        if (exhibit.qr_code_url) {
          setQrCodeUrl(exhibit.qr_code_url);
        }
        if (exhibit.code) {
          setExhibitCode(exhibit.code);
        }
        setInitialLoad(true);
      }
    } catch (error) {
      toast.error('Failed to load exhibit');
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Image must be less than 5MB');
        return;
      }
      setImageFile(file);
      setImagePreview(URL.createObjectURL(file));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      let exhibitId = id;

      // Create or update exhibit
      if (isEdit) {
        await axios.put(
          `${API}/exhibits/${id}`,
          formData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Exhibit updated');
      } else {
        const response = await axios.post(
          `${API}/exhibits`,
          formData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        exhibitId = response.data.id;
        setExhibitCode(response.data.code);
        toast.success('Exhibit created');
      }

      // Upload image if selected
      if (imageFile) {
        const formDataImage = new FormData();
        formDataImage.append('file', imageFile);
        await axios.post(
          `${API}/exhibits/${exhibitId}/upload-image`,
          formDataImage,
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }

      navigate('/staff/dashboard');
    } catch (error) {
      toast.error('Failed to save exhibit');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateQR = async () => {
    if (!id) {
      toast.error('Please save the exhibit first');
      return;
    }

    try {
      const response = await axios.post(
        `${API}/exhibits/${id}/generate-qr`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setQrCodeUrl(response.data.qr_code_url);
      toast.success('QR code generated');
    } catch (error) {
      toast.error('Failed to generate QR code');
    }
  };

  const downloadQRCode = () => {
    if (!qrCodeUrl) return;

    const link = document.createElement('a');
    link.href = qrCodeUrl;
    link.download = `exhibit-${id}-qr.png`;
    link.click();
  };

  const handleAutoTranslate = async () => {
    if (!formData.title_tc || !formData.description_tc) {
      toast.error('Please fill in Traditional Chinese title and description first');
      return;
    }

    setTranslating(true);
    try {
      const response = await axios.post(
        `${API}/exhibits/translate`,
        {
          title_tc: formData.title_tc,
          description_tc: formData.description_tc
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setFormData({
        ...formData,
        title_en: response.data.title_en,
        description_en: response.data.description_en,
        title_sc: response.data.title_sc,
        description_sc: response.data.description_sc
      });

      toast.success('Translations generated successfully!');
    } catch (error) {
      toast.error('Translation failed. Please try again or fill manually.');
    } finally {
      setTranslating(false);
    }
  };

  const handlePreviewAudio = (language, text) => {
    if (!text || text.trim() === '') {
      toast.error('No description to preview');
      return;
    }

    // Stop any currently playing audio
    if (playingAudio) {
      window.speechSynthesis.cancel();
      if (playingAudio === language) {
        setPlayingAudio(null);
        return;
      }
    }

    // Ensure voices are loaded
    const voices = window.speechSynthesis.getVoices();
    
    const playPreview = () => {
      // Set up language-specific voice
      const utterance = new SpeechSynthesisUtterance(text);
      
      if (language === 'tc') {
        utterance.lang = 'zh-HK'; // Cantonese
      } else if (language === 'sc') {
        utterance.lang = 'zh-CN'; // Mandarin
      } else {
        utterance.lang = 'en-US';
      }
      
      // Try to select the best voice
      const currentVoices = window.speechSynthesis.getVoices();
      let selectedVoice = null;
      
      if (language === 'tc') {
        selectedVoice = currentVoices.find(v => 
          v.lang.includes('zh-HK') || v.lang.includes('zh-TW') || v.lang.includes('yue-HK')
        );
      } else if (language === 'sc') {
        selectedVoice = currentVoices.find(v => 
          v.lang.includes('zh-CN') || v.name.includes('Chinese (China)')
        );
      } else {
        selectedVoice = currentVoices.find(v => 
          v.lang.includes('en-US') || v.lang.includes('en-GB')
        );
      }
      
      if (selectedVoice) {
        utterance.voice = selectedVoice;
      }
      
      utterance.rate = 0.9;
      utterance.pitch = 1;
      
      utterance.onstart = () => {
        setPlayingAudio(language);
      };
      
      utterance.onend = () => {
        setPlayingAudio(null);
      };
      
      utterance.onerror = (event) => {
        setPlayingAudio(null);
        console.error('TTS Error:', event);
        toast.error('Audio playback failed');
      };
      
      window.speechSynthesis.speak(utterance);
    };

    if (voices.length === 0) {
      // Wait for voices to load
      window.speechSynthesis.onvoiceschanged = () => {
        playPreview();
      };
      window.speechSynthesis.getVoices();
    } else {
      playPreview();
    }
  };

  const handleGenerateAllAudio = () => {
    const languages = [
      { lang: 'tc', text: formData.description_tc, name: '繁體中文' },
      { lang: 'en', text: formData.description_en, name: 'English' },
      { lang: 'sc', text: formData.description_sc, name: '简体中文' }
    ];

    const missingDescriptions = languages.filter(l => !l.text || l.text.trim() === '');
    
    if (missingDescriptions.length === 3) {
      toast.error('Please add descriptions first');
      return;
    }

    if (missingDescriptions.length > 0) {
      toast.warning(`Missing descriptions: ${missingDescriptions.map(l => l.name).join(', ')}`);
    }

    toast.success('Audio preview ready! Use the 🔊 buttons to preview each language.');
  };

  const handleRegenerateCode = async () => {
    if (!id) {
      toast.error('Please save the exhibit first');
      return;
    }

    if (!window.confirm('Regenerate exhibit code? This will change the code visitors use to access this exhibit.')) {
      return;
    }

    try {
      const response = await axios.post(
        `${API}/exhibits/${id}/regenerate-code`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setExhibitCode(response.data.code);
      toast.success(`New code generated: ${response.data.code}`);
    } catch (error) {
      toast.error('Failed to regenerate code');
    }
  };

  return (
    <div className="min-h-screen bg-background pb-12" data-testid="exhibit-form-page">
      {/* Header */}
      <div className="bg-white border-b border-border">
        <div className="max-w-4xl mx-auto px-6 py-4">
          <div className="flex items-center gap-3">
            <Button
              onClick={() => navigate('/staff/dashboard')}
              variant="ghost"
              size="icon"
              data-testid="back-button"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <h1 className="text-2xl font-heading font-bold">
              {isEdit ? 'Edit Exhibit' : 'Create New Exhibit'}
            </h1>
          </div>
        </div>
      </div>

      {/* Form */}
      <div className="max-w-4xl mx-auto px-6 py-8">
        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Image Upload */}
          <div className="space-y-4">
            <Label>Exhibit Image</Label>
            <div className="flex flex-col gap-4">
              {imagePreview && (
                <div className="w-full max-w-md">
                  <img
                    src={imagePreview}
                    alt="Preview"
                    className="w-full h-64 object-cover rounded-lg border border-border"
                    data-testid="image-preview"
                  />
                </div>
              )}
              <div>
                <Input
                  type="file"
                  accept="image/jpeg,image/png"
                  onChange={handleImageChange}
                  data-testid="image-upload-input"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  JPG or PNG, max 5MB
                </p>
              </div>
            </div>
          </div>

          {/* 3-Digit Code Section */}
          {exhibitCode && (
            <div className="space-y-4 p-6 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-heading font-semibold text-blue-900">3-Digit Exhibit Code</h3>
                  <p className="text-sm text-blue-700 mt-1">Visitors can use this code to access the exhibit</p>
                </div>
                <Button
                  type="button"
                  onClick={handleRegenerateCode}
                  variant="outline"
                  size="sm"
                  className="bg-white"
                  data-testid="regenerate-code-button"
                >
                  Regenerate
                </Button>
              </div>
              <div className="flex items-center gap-4">
                <div className="flex-1 bg-white rounded-lg border-2 border-blue-300 p-6 text-center">
                  <div className="font-mono text-6xl font-bold text-primary tracking-widest" data-testid="exhibit-code-display">
                    {exhibitCode}
                  </div>
                </div>
              </div>
              <p className="text-xs text-blue-700">
                ℹ️ This code is displayed on signage alongside the QR code. Regenerating will change the access code.
              </p>
            </div>
          )}

          {/* Traditional Chinese */}
          <div className="space-y-4 p-6 bg-white border border-border rounded-lg">
            <div className="flex items-center justify-between">
              <h3 className="font-heading font-semibold">繁體中文 (Traditional Chinese)</h3>
              <Button
                type="button"
                onClick={handleAutoTranslate}
                disabled={translating || !formData.title_tc || !formData.description_tc}
                variant="outline"
                size="sm"
                data-testid="auto-translate-button"
              >
                {translating ? (
                  <>
                    <span className="animate-spin mr-2">⟳</span>
                    Translating...
                  </>
                ) : (
                  <>
                    <span className="mr-2">🌐</span>
                    Auto Generate EN & SC
                  </>
                )}
              </Button>
            </div>
            <div className="space-y-2">
              <Label htmlFor="title_tc">Title *</Label>
              <Input
                id="title_tc"
                value={formData.title_tc}
                onChange={(e) => setFormData({ ...formData, title_tc: e.target.value })}
                required
                data-testid="title-tc-input"
              />
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="description_tc">Description * (max 2000 characters)</Label>
                <Button
                  type="button"
                  onClick={() => handlePreviewAudio('tc', formData.description_tc)}
                  variant="ghost"
                  size="sm"
                  disabled={!formData.description_tc}
                  data-testid="preview-audio-tc"
                >
                  {playingAudio === 'tc' ? (
                    <>
                      <VolumeX className="w-4 h-4 mr-1" />
                      Stop
                    </>
                  ) : (
                    <>
                      <Volume2 className="w-4 h-4 mr-1" />
                      Preview Audio
                    </>
                  )}
                </Button>
              </div>
              <Textarea
                id="description_tc"
                value={formData.description_tc}
                onChange={(e) => setFormData({ ...formData, description_tc: e.target.value })}
                maxLength={2000}
                rows={4}
                required
                data-testid="description-tc-input"
              />
              <p className="text-xs text-muted-foreground">
                {(formData.description_tc || '').length}/2000 characters
              </p>
            </div>
            <div className='space-y-2'>
              <Label htmlFor="voice_url_tc">Voice URL *</Label>
              <Input
                id="voice_url_tc"
                value={formData.voice_url_tc}
                onChange={(e) => setFormData({ ...formData, voice_url_tc: e.target.value })}
                required
                data-testid="voice-url-tc-input"
              />
            </div>
          </div>

          {/* English */}
          <div className="space-y-4 p-6 bg-white border border-border rounded-lg">
            <h3 className="font-heading font-semibold">English</h3>
            <div className="space-y-2">
              <Label htmlFor="title_en">Title</Label>
              <Input
                id="title_en"
                value={formData.title_en}
                onChange={(e) => setFormData({ ...formData, title_en: e.target.value })}
                data-testid="title-en-input"
              />
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="description_en">Description (max 2000 characters)</Label>
                <Button
                  type="button"
                  onClick={() => handlePreviewAudio('en', formData.description_en)}
                  variant="ghost"
                  size="sm"
                  disabled={!formData.description_en}
                  data-testid="preview-audio-en"
                >
                  {playingAudio === 'en' ? (
                    <>
                      <VolumeX className="w-4 h-4 mr-1" />
                      Stop
                    </>
                  ) : (
                    <>
                      <Volume2 className="w-4 h-4 mr-1" />
                      Preview Audio
                    </>
                  )}
                </Button>
              </div>
              <Textarea
                id="description_en"
                value={formData.description_en}
                onChange={(e) => setFormData({ ...formData, description_en: e.target.value })}
                maxLength={2000}
                rows={4}
                data-testid="description-en-input"
              />
            </div>
            <div className='space-y-2'>
              <Label htmlFor="voice_url_en">Voice URL *</Label>
              <Input
                id="voice_url_en"
                value={formData.voice_url_en}
                onChange={(e) => setFormData({ ...formData, voice_url_en: e.target.value })}
                required
                data-testid="voice-url-en-input"
              />
            </div>
          </div>

          {/* Simplified Chinese */}
          <div className="space-y-4 p-6 bg-white border border-border rounded-lg">
            <h3 className="font-heading font-semibold">简体中文 (Simplified Chinese)</h3>
            <div className="space-y-2">
              <Label htmlFor="title_sc">Title</Label>
              <Input
                id="title_sc"
                value={formData.title_sc}
                onChange={(e) => setFormData({ ...formData, title_sc: e.target.value })}
                data-testid="title-sc-input"
              />
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="description_sc">Description (max 2000 characters)</Label>
                <Button
                  type="button"
                  onClick={() => handlePreviewAudio('sc', formData.description_sc)}
                  variant="ghost"
                  size="sm"
                  disabled={!formData.description_sc}
                  data-testid="preview-audio-sc"
                >
                  {playingAudio === 'sc' ? (
                    <>
                      <VolumeX className="w-4 h-4 mr-1" />
                      Stop
                    </>
                  ) : (
                    <>
                      <Volume2 className="w-4 h-4 mr-1" />
                      Preview Audio
                    </>
                  )}
                </Button>
              </div>
              <Textarea
                id="description_sc"
                value={formData.description_sc}
                onChange={(e) => setFormData({ ...formData, description_sc: e.target.value })}
                maxLength={2000}
                rows={4}
                data-testid="description-sc-input"
              />
            </div>
            <div className='space-y-2'>
              <Label htmlFor="voice_url_sc">Voice URL *</Label>
              <Input
                id="voice_url_sc"
                value={formData.voice_url_sc}
                onChange={(e) => setFormData({ ...formData, voice_url_sc: e.target.value })}
                required
                data-testid="voice-url-sc-input"
              />
            </div>
          </div>

          {/* Audio Preview Section */}
          <div className="space-y-4 p-6 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-heading font-semibold text-blue-900">Audio Preview</h3>
                <p className="text-sm text-blue-700 mt-1">Test how descriptions will sound to visitors</p>
              </div>
              <Button
                type="button"
                onClick={handleGenerateAllAudio}
                variant="outline"
                className="bg-white"
                data-testid="check-all-audio-button"
              >
                <Volume2 className="w-4 h-4 mr-2" />
                Check All Audio
              </Button>
            </div>
            <div className="text-sm text-blue-700">
              Use the 🔊 <strong>Preview Audio</strong> buttons above to test each language&apos;s description with browser text-to-speech.
            </div>
          </div>

          {/* QR Code Section */}
          {isEdit && (
            <div className="space-y-4 p-6 bg-white border border-border rounded-lg">
              <h3 className="font-heading font-semibold">QR Code</h3>
              {qrCodeUrl ? (
                <div className="flex items-start gap-4">
                  <div className="p-4 bg-white border border-border rounded-lg">
                    <img src={qrCodeUrl} alt="QR Code" className="w-32 h-32" data-testid="qr-code-preview" />
                  </div>
                  <Button
                    type="button"
                    onClick={downloadQRCode}
                    variant="outline"
                    data-testid="download-qr-button"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Download QR Code
                  </Button>
                </div>
              ) : (
                <Button
                  type="button"
                  onClick={handleGenerateQR}
                  variant="outline"
                  data-testid="generate-qr-button"
                >
                  Generate QR Code
                </Button>
              )}
            </div>
          )}

          {/* Submit */}
          <div className="flex gap-3">
            <Button
              type="submit"
              disabled={loading}
              className="px-8"
              data-testid="save-exhibit-button"
            >
              {loading ? 'Saving...' : isEdit ? 'Update Exhibit' : 'Create Exhibit'}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate('/staff/dashboard')}
              data-testid="cancel-button"
            >
              Cancel
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ExhibitForm;